import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Text } from "~/components/text";

export interface SearchTypeOption<T extends string = string> {
  value: T;
  label: string;
}

interface SearchTypeSelectProps<T extends string = string> {
  value?: T;
  onValueChange?: (value: T) => void;
  options: SearchTypeOption<T>[];
  placeholder?: string;
  disabled?: boolean;
}

export const SearchTypeSelect = <T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder = "Search type",
  disabled = false,
}: SearchTypeSelectProps<T>) => {
  // If there's only one option, show it as a static display
  if (options.length === 1) {
    const singleOption = options[0];
    return (
      <div
        className="h-full border border-secondary-300 rounded-lg rounded-e-none bg-secondary-50 flex items-center px-3 py-2 whitespace-nowrap"
        data-testid="search-type-static"
      >
        <Text as="span" variant="body">
          {singleOption.label}
        </Text>
      </div>
    );
  }

  // For multiple options, show the dropdown
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="h-full border-secondary-300 rounded-lg rounded-e-none bg-secondary-50 min-w-40"
        data-testid="search-dropdown"
        disabled={disabled}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            data-testid={`search-dropdown-item-${option.value}`}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
